import { useSessionKey } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import type {
  ClinicRosterToday,
  HouseholdMember,
  HouseholdOnRoster,
} from "@perception/perceptionClient";
import { useClinicRoster } from "@perception/perceptionQueries";
import {
  BoardLane,
  EmptyNote,
  LabelTape,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  PanelError,
  Skeleton,
} from "@scribenote/brian-design-system";
import { formatRelativeTimeMs, formatTime } from "@utils/format-date";
import { useNow } from "@utils/use-now";
import type { ReactNode } from "react";

// Fixed shares so the staff and household tables line up one above the other.
const COLUMNS = [
  { title: "Name", width: "w-[30%]" },
  { title: "Role", width: "w-[22%]" },
  { title: "Location", width: "w-[22%]" },
  { title: "Last seen", width: "w-[26%]" },
] as const;

const LastSeen = ({ at }: { at: string }) => {
  useNow();
  return (
    <span className="text-mono-time">
      {formatTime(at)}
      <span className="text-annotation">
        {" · "}
        {formatRelativeTimeMs(Date.parse(at))}
      </span>
    </span>
  );
};

const RosterRow = ({
  name,
  role,
  room,
  lastSeenAt,
}: {
  name: string;
  role: string;
  room: string;
  lastSeenAt: string;
}) => (
  <LedgerRow>
    <LedgerCell className="font-medium text-ink">{name}</LedgerCell>
    <LedgerCell>{role}</LedgerCell>
    <LedgerCell>
      <LabelTape className="max-w-[12rem] truncate align-middle">
        {room}
      </LabelTape>
    </LedgerCell>
    <LedgerCell>
      <LastSeen at={lastSeenAt} />
    </LedgerCell>
  </LedgerRow>
);

/** `children` are the table's bodies: one for staff, one per household. */
const RosterLane = ({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string | false;
  children: ReactNode;
}) => (
  <BoardLane title={title}>
    {empty ? (
      <EmptyNote className="p-4">{empty}</EmptyNote>
    ) : (
      <LedgerTable>
        <colgroup>
          {COLUMNS.map(({ title, width }) => (
            <col key={title} className={width} />
          ))}
        </colgroup>
        <LedgerHead>
          <tr>
            {COLUMNS.map(({ title }) => (
              <LedgerHeadCell key={title}>{title}</LedgerHeadCell>
            ))}
          </tr>
        </LedgerHead>
        {children}
      </LedgerTable>
    )}
  </BoardLane>
);

const memberRole = (member: HouseholdMember): string =>
  member.kind === "client"
    ? "Client"
    : member.species
      ? `Patient · ${member.species}`
      : "Patient";

const HouseholdBody = ({ household }: { household: HouseholdOnRoster }) => (
  <LedgerBody>
    <tr className="border-b border-rule">
      <th
        scope="rowgroup"
        colSpan={COLUMNS.length}
        className="bg-card px-3 pt-3 pb-1.5 text-left text-mono-caps text-fine-print text-ink-body"
      >
        {household.label}
      </th>
    </tr>
    {household.members.map((member) => (
      <RosterRow
        key={`${member.kind}:${member.uuid}`}
        name={member.name}
        role={memberRole(member)}
        room={member.room_name}
        lastSeenAt={member.last_seen_at}
      />
    ))}
  </LedgerBody>
);

export const ClinicRosterTables = ({
  roster,
}: {
  roster: ClinicRosterToday;
}) => (
  <div className="flex flex-col gap-[22px]" data-dd-privacy="hidden">
    <RosterLane
      title="Staff in the clinic today"
      empty={roster.staff.length === 0 && "No staff identified yet today."}
    >
      <LedgerBody>
        {roster.staff.map((s) => (
          <RosterRow
            key={s.user_uuid}
            name={s.name}
            role={s.role ?? "Staff"}
            room={s.room_name}
            lastSeenAt={s.last_seen_at}
          />
        ))}
      </LedgerBody>
    </RosterLane>
    <RosterLane
      title="Clients and patients today"
      empty={
        roster.households.length === 0 &&
        "No clients or patients identified yet today."
      }
    >
      {roster.households.map((household) => (
        <HouseholdBody key={household.household_uuid} household={household} />
      ))}
    </RosterLane>
  </div>
);

const StaffRoster = () => (
  <ClinicRosterTables roster={useClinicRoster(useSessionKey()).data} />
);

/** Who perception has identified in the clinic today, loaded for a staff session. */
export const ClinicRoster = () => (
  <AsyncBoundary
    skeleton={<Skeleton shape="sheet" className="h-24" />}
    fallback={(retry) => (
      <PanelError label="the clinic roster" onRetry={retry} />
    )}
  >
    <StaffRoster />
  </AsyncBoundary>
);
